import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  Output,
  ViewChild,
} from '@angular/core';
import { first } from 'rxjs/operators';

import { List, ListEntryTypes } from '@site-mate/dashpivot-shared-library';

import { AppHierarchy } from 'app/shared/model/app-hierarchy.enum';
import { TextInputComponent } from 'app/ui-components/text-input/text-input.component';

import { ListPropertyService } from '../list-property.service';

@Component({
  selector: 'cc-list-property-add',
  templateUrl: './list-property-add.component.html',
  styleUrls: ['./list-property-add.component.scss'],
})
export class ListPropertyAddComponent {
  @Input() list: List;
  @Input() parentId: string;
  @Input() hierarchy: Exclude<AppHierarchy, AppHierarchy.Team>;
  @Input() addIndex: number;
  @Output() readonly propertyCreated = new EventEmitter();
  @Output() readonly propertyCancelled = new EventEmitter();

  @ViewChild(TextInputComponent) propertyNameInput: TextInputComponent;

  readonly listEntryTypes = ListEntryTypes;
  isInCreateMode: boolean;
  selectedKind: ListEntryTypes | null = null;
  propertyName = '';
  loading = false;

  constructor(
    private readonly host: ElementRef,
    private readonly zone: NgZone,
    private readonly listPropertyService: ListPropertyService,
  ) {}

  startCreateMode() {
    this.isInCreateMode = true;
  }

  selectKind(kind: ListEntryTypes) {
    this.selectedKind = kind;
    this.zone.onStable.pipe(first()).subscribe(() => {
      if (this.propertyName === '') {
        this.propertyNameInput.focus();
      }
    });
  }

  propertyNameUpdated(newListName: string) {
    this.propertyName = newListName;
  }

  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.finishNewProperty();
    } else if (event.key === 'Escape') {
      this.cancelPropertyCreation();
    }
  }

  @HostListener('window:click', ['$event']) onWindowClick(event: MouseEvent) {
    const clickedOutside = !this.host.nativeElement.contains(event.target);
    const isAddPropertyButton =
      event.target instanceof HTMLElement && event.target.dataset.actionButton === 'addListProperty';
    if (clickedOutside && this.isInCreateMode && !isAddPropertyButton) {
      this.finishNewProperty();
    }
  }

  private finishNewProperty() {
    if (this.propertyName !== '') {
      this.saveNewProperty();
    } else {
      this.cancelPropertyCreation();
    }
  }

  private cancelPropertyCreation() {
    this.resetNewProperty();
    this.propertyCancelled.emit();
  }

  private saveNewProperty() {
    this.addProperty();
    this.resetNewProperty();
  }

  private resetNewProperty() {
    this.isInCreateMode = false;
    this.selectedKind = null;
    this.propertyName = '';
  }

  addProperty() {
    const index = this.addIndex ?? this.list.properties.length;
    const newProperty = { name: this.propertyName, kind: this.selectedKind, index };
    this.loading = true;

    this.listPropertyService
      .create(this.list._id, newProperty, this.parentId, this.hierarchy)
      .subscribe((updatedList) => {
        this.propertyCreated.emit(updatedList);
        this.loading = false;
      });
  }
}
